import { AnimatePresence, motion } from "motion/react";
import { Heart, ShoppingBag, Zap } from "lucide-react";

export default function StickyPurchaseBar({
  product,
  wished = false,
  onAddToCart,
  onBuyNow,
  onWishlist,
}) {
  if (!product) return null;

  const soldOut =
    product.isSoldOut || Number(product.stock) <= 0;

  const image =
    product.image ||
    product.gallery?.[0]?.src ||
    null;

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 80 }}
        transition={{
          duration: 0.3,
          ease: "easeOut",
        }}
        aria-label="Product purchase actions"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-black/10 bg-white/95 p-3 shadow-[0_-12px_40px_rgba(0,0,0,0.12)] backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto flex max-w-screen-xl items-center gap-3">
          <div className="hidden min-w-0 flex-1 items-center gap-3 sm:flex">
            {image && (
              <img
                src={image}
                alt={product.title}
                className="size-12 shrink-0 rounded-xl object-cover"
              />
            )}

            <div className="min-w-0">
              <p className="truncate text-sm font-black">
                {product.title}
              </p>

              <p className="text-sm font-bold text-red-600">
                ৳
                {Number(
                  product.price || 0
                ).toLocaleString()}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onWishlist}
            aria-pressed={wished}
            aria-label={
              wished
                ? `Remove ${product.title} from wishlist`
                : `Add ${product.title} to wishlist`
            }
            className={`inline-flex size-11 shrink-0 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
              wished
                ? "border-red-200 bg-red-50 text-red-600"
                : "border-black/10 bg-white text-black"
            }`}
          >
            <Heart
              className={`size-5 ${
                wished
                  ? "fill-current"
                  : ""
              }`}
            />
          </button>

          <button
            type="button"
            onClick={onAddToCart}
            disabled={soldOut}
            aria-label={
              soldOut
                ? `${product.title} is sold out`
                : `Add ${product.title} to cart`
            }
            className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full border border-black bg-white px-4 py-3 text-xs font-black uppercase text-black transition hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-100 disabled:text-neutral-400 sm:flex-none sm:px-6"
          >
            <ShoppingBag className="size-4 shrink-0" />

            <span className="truncate">
              {soldOut ? "Sold Out" : "Add to Cart"}
            </span>
          </button>

          <button
            type="button"
            onClick={onBuyNow}
            disabled={soldOut}
            aria-label={`Buy ${product.title} now`}
            className="hidden items-center justify-center gap-2 rounded-full bg-black px-6 py-3 text-xs font-black uppercase text-white transition hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-300 xs:inline-flex sm:inline-flex"
          >
            <Zap className="size-4" />
            Buy Now
          </button>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}